import type { ClassId, SynergyEffect } from '@prism/sim';
import type { UnlockNode } from '../schemas/unlocks.js';
import { TOWERS } from './towers.js';
import { CLASSES } from './classes.js';

/**
 * The meta unlock tree (TRD-08 Part B) — ~1000 nodes, radial.
 *
 * ── Shape ─────────────────────────────────────────────────────────────────────────────
 * A circle (R-08-18a). The player's four starting classes and six locked ones are ten
 * spokes radiating from the centre at 30° steps, each spoke a chain of that class's
 * upgrades ending in a keystone; the last two clock positions are the economy and
 * defense spokes. Locked classes put their unlock node FIRST on the spoke — everything
 * behind it is gated on owning the class, which makes the DAG read at a glance: buy
 * inward-out along a direction you care about.
 *
 * The 10x expansion (R-08-18b) layers three things onto each wedge:
 *  - LADDERS: four chains of 17 one-Shard stat steps flanking each trunk at ±7°/±13°,
 *    each ending in a 3-Shard notable — the cheap, always-something-to-buy filler that
 *    makes every run's Shards spendable.
 *  - TECHNIQUES: three hand-authored ability nodes per class hanging off mid-ladder —
 *    the everyNthShot/onKill/aura-kind effects that change HOW a class plays.
 *  - SYNTHESIS: at each of the 12 boundaries between wedges, a Confluence (requires a
 *    mid-trunk node from BOTH neighbours) and an Apex (requires BOTH keystones). These
 *    are the connecting points: own two directions and the seam between them opens a
 *    combined ability neither side has alone. `requires` is already ALL-semantics, so
 *    an AND-gate is plain data.
 *
 * ── Cross-class effects and the forClass constraint ───────────────────────────────────
 * A node's payload carries ONE optional forClass, and a global bundle must stick to
 * class-free effect kinds (see synergy.ts's runEffects pass). So every synthesis node
 * either scopes to one of its two classes while referencing the OTHER's status —
 * "Venom towers hit chilled enemies harder" — or uses genuinely global kinds
 * (runStat, statusTuning, allTowers stats, periodic fields, gold).
 *
 * ── Built, not hand-written ───────────────────────────────────────────────────────────
 * A thousand literals would be unreviewable. The builders below are plain deterministic
 * data-code: same input, same tree, and `pnpm validate:content` (V-16..21) checks the
 * result like any authored content — including V-21's minimum node spacing, which is
 * what keeps a generated layout from silently stacking dots. Positions are polar → the
 * 0-100 layout space; content is allowed trig, only the sim is not.
 *
 * ── Budget ────────────────────────────────────────────────────────────────────────────
 * Total cost lands around ~1670 Shards under an 1800 ceiling (V-18, R-08-20b). Shard
 * awards were rescaled alongside (meta.ts): a decent run pays 10-15, a first full win
 * ~40, so the FULL circle is a long project by design — but every ladder pays out from
 * its first node, and respec is free.
 */

const CENTER = 50;
/** Spoke slot radii, centre-out. Keystone at 49 keeps every coordinate inside 0-100. */
const RADII = [13, 20, 27, 33, 39, 45, 45, 49] as const;
/** The two branch slots share a radius and fan ±10° off the spoke. */
const BRANCH_SLOTS: Record<number, number> = { 5: -10, 6: 10 };

function polar(angleDeg: number, radius: number): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: Math.round((CENTER + radius * Math.cos(rad)) * 10) / 10,
    y: Math.round((CENTER + radius * Math.sin(rad)) * 10) / 10,
  };
}

/** A chain of nodes along one direction; each node requires the one before it. */
function spoke(angleDeg: number, nodes: Omit<UnlockNode, 'position' | 'requires'>[]): UnlockNode[] {
  return nodes.map((node, i) => ({
    ...node,
    requires: i === 0 ? [] : [nodes[i - 1]?.id as string],
    position: polar(angleDeg + (BRANCH_SLOTS[i] ?? 0), RADII[i] ?? 49),
  }));
}

const stat = (
  statName:
    | 'flatDamage'
    | 'damageMultiplier'
    | 'attackSpeedMultiplier'
    | 'rangeBonus'
    | 'rangeMultiplier'
    | 'critChance'
    | 'critMultiplier'
    | 'armorPierce'
    | 'percentMaxHpDamage'
    | 'chainCount'
    | 'pierceCount',
  op: 'add' | 'mul',
  value: number,
): SynergyEffect => ({ k: 'stat', target: { s: 'sameClass' }, stat: statName, op, value });

/**
 * Per-class flavour branch: the node that makes a spoke feel like ITS class rather
 * than a stat column with a tint. This is where the specific, juicy effects live.
 */
const FLAVOUR: Record<ClassId, { name: string; summary: string; node: UnlockNode['payload'] }> = {
  warden: {
    name: 'Bulwark',
    summary: 'The Core takes the field with +4 HP.',
    node: { k: 'effect', effects: [{ k: 'runStat', stat: 'bonusCoreHp', op: 'add', value: 4 }] },
  },
  marksman: {
    name: 'Longshot',
    summary: 'Marksman towers reach +0.6 tiles further.',
    node: { k: 'effect', forClass: 'marksman', effects: [stat('rangeBonus', 'add', 0.6)] },
  },
  arcanist: {
    name: 'Unravel',
    summary: 'Arcanist towers ignore 25% of armor.',
    node: { k: 'effect', forClass: 'arcanist', effects: [stat('armorPierce', 'add', 0.25)] },
  },
  venom: {
    name: 'Virulence',
    summary: 'Poison ticks twice as fast, on every source.',
    node: {
      k: 'effect',
      effects: [{ k: 'statusTuning', status: 'poison', tickIntervalTicks: 15 }],
    },
  },
  frost: {
    name: 'Deep Chill',
    summary: 'Frost towers hit chilled enemies 15% harder.',
    node: {
      k: 'effect',
      forClass: 'frost',
      effects: [{ k: 'bonusVsStatus', status: 'chill', damageMultiplier: 1.15 }],
    },
  },
  tempest: {
    name: 'Conduction',
    summary: 'Tempest chains hop to one extra target.',
    node: { k: 'effect', forClass: 'tempest', effects: [stat('chainCount', 'add', 1)] },
  },
  alchemist: {
    name: 'Bulk Discount',
    summary: 'Shop rerolls cost 15% less, always.',
    node: {
      k: 'effect',
      effects: [{ k: 'runStat', stat: 'rerollCostMultiplier', op: 'mul', value: 0.85 }],
    },
  },
  beacon: {
    name: 'Focus',
    summary: 'Beacon crits hit for +25% more.',
    node: { k: 'effect', forClass: 'beacon', effects: [stat('critMultiplier', 'add', 0.25)] },
  },
  swarm: {
    name: 'Frenzy',
    summary: 'Swarm towers attack 10% faster.',
    node: { k: 'effect', forClass: 'swarm', effects: [stat('attackSpeedMultiplier', 'mul', 1.1)] },
  },
  void: {
    name: 'Hunger',
    summary: 'Void hits carve off an extra 1% of max HP.',
    node: { k: 'effect', forClass: 'void', effects: [stat('percentMaxHpDamage', 'add', 0.01)] },
  },
};

/** Locked classes buy their way in; the spoke's first node is the class itself. */
const LOCKED_CLASS_COST: Partial<Record<ClassId, number>> = {
  arcanist: 4,
  tempest: 4,
  alchemist: 5,
  beacon: 5,
  swarm: 6,
  void: 6,
};

function classSpoke(classId: ClassId, angleDeg: number): UnlockNode[] {
  const cls = CLASSES.find((c) => c.id === classId);
  const name = cls?.name ?? classId;
  const unlockCost = LOCKED_CLASS_COST[classId];
  const flavour = FLAVOUR[classId];

  const entry: Omit<UnlockNode, 'position' | 'requires'> =
    unlockCost !== undefined
      ? {
          id: `class-${classId}`,
          kind: 'class',
          name,
          summary: `Unlocks ${name} and its 6 towers. ${cls?.description ?? ''}`.trim(),
          cost: unlockCost,
          payload: { k: 'class', classId },
        }
      : {
          id: `${classId}-power-1`,
          kind: 'effect',
          name: `${name} Power`,
          summary: `${name} towers deal 8% more damage.`,
          cost: 2,
          payload: {
            k: 'effect',
            forClass: classId,
            effects: [stat('damageMultiplier', 'mul', 1.08)],
          },
        };

  return spoke(angleDeg, [
    entry,
    {
      id: `${classId}-haste`,
      kind: 'effect',
      name: `${name} Haste`,
      summary: `${name} towers attack 8% faster.`,
      cost: 3,
      payload: {
        k: 'effect',
        forClass: classId,
        effects: [stat('attackSpeedMultiplier', 'mul', 1.08)],
      },
    },
    {
      id: `${classId}-reach`,
      kind: 'effect',
      name: `${name} Reach`,
      summary: `${name} towers gain +0.4 tiles of range.`,
      cost: 3,
      payload: { k: 'effect', forClass: classId, effects: [stat('rangeBonus', 'add', 0.4)] },
    },
    {
      id: `${classId}-power-2`,
      kind: 'effect',
      name: `${name} Power II`,
      summary: `${name} towers deal a further 10% more damage.`,
      cost: 4,
      payload: { k: 'effect', forClass: classId, effects: [stat('damageMultiplier', 'mul', 1.1)] },
    },
    {
      id: `${classId}-precision`,
      kind: 'effect',
      name: `${name} Precision`,
      summary: `${name} towers gain +5% crit chance.`,
      cost: 4,
      payload: { k: 'effect', forClass: classId, effects: [stat('critChance', 'add', 0.05)] },
    },
    {
      id: `${classId}-impact`,
      kind: 'effect',
      name: `${name} Impact`,
      summary: `${name} towers hit for +2 flat damage.`,
      cost: 4,
      payload: { k: 'effect', forClass: classId, effects: [stat('flatDamage', 'add', 2)] },
    },
    {
      id: `${classId}-${flavour.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
      kind: 'effect',
      name: flavour.name,
      summary: flavour.summary,
      cost: 5,
      payload: flavour.node,
    },
    {
      id: `${classId}-keystone`,
      kind: 'effect',
      name: `${name} Mastery`,
      summary: `${name} towers deal 15% more damage and attack 10% faster.`,
      cost: 5,
      payload: {
        k: 'effect',
        forClass: classId,
        effects: [stat('damageMultiplier', 'mul', 1.15), stat('attackSpeedMultiplier', 'mul', 1.1)],
      },
    },
  ]);
}

const run = (
  statName:
    | 'goldPerWave'
    | 'interestCap'
    | 'rerollCostMultiplier'
    | 'bonusPlacements'
    | 'bonusCoreHp'
    | 'goldDropChance',
  op: 'add' | 'mul',
  value: number,
): SynergyEffect => ({ k: 'runStat', stat: statName, op, value });

/** Economy spoke: gold, shop, and the two locked maps. */
function economySpoke(angleDeg: number): UnlockNode[] {
  const chain = spoke(angleDeg, [
    {
      id: 'eco-purse-1',
      kind: 'start',
      name: 'Seed Money',
      summary: 'Free-play runs start with +2 gold.',
      cost: 2,
      payload: { k: 'start', stat: 'gold', value: 2 },
    },
    {
      id: 'eco-purse-2',
      kind: 'start',
      name: 'War Chest',
      summary: 'Free-play runs start with +3 more gold.',
      cost: 3,
      payload: { k: 'start', stat: 'gold', value: 3 },
    },
    {
      id: 'eco-interest',
      kind: 'effect',
      name: 'Compound Interest',
      summary: 'Interest pays on one more gold of savings.',
      cost: 4,
      payload: { k: 'effect', effects: [run('interestCap', 'add', 1)] },
    },
    {
      id: 'eco-tithe-1',
      kind: 'effect',
      name: 'Tithe',
      summary: 'Every cleared wave pays +1 bonus gold.',
      cost: 4,
      payload: { k: 'effect', effects: [run('goldPerWave', 'add', 1)] },
    },
    {
      id: 'eco-lucky',
      kind: 'effect',
      name: 'Lucky Strikes',
      summary: 'Kills have +5% chance to drop bonus gold.',
      cost: 4,
      payload: { k: 'effect', effects: [run('goldDropChance', 'add', 0.05)] },
    },
    {
      id: 'eco-tithe-2',
      kind: 'effect',
      name: 'Tribute',
      summary: 'Every cleared wave pays a further +1 gold.',
      cost: 5,
      payload: { k: 'effect', effects: [run('goldPerWave', 'add', 1)] },
    },
    {
      id: 'eco-bench',
      kind: 'start',
      name: 'Deep Bench',
      summary: 'The bench holds one more tower.',
      cost: 5,
      payload: { k: 'start', stat: 'benchSlot', value: 1 },
    },
    {
      id: 'eco-keystone',
      kind: 'effect',
      name: 'Golden Age',
      summary: 'Waves pay +2 gold and interest covers one more.',
      cost: 6,
      payload: {
        k: 'effect',
        effects: [run('goldPerWave', 'add', 2), run('interestCap', 'add', 1)],
      },
    },
  ]);
  // The maps hang off the first purse node as a short side-branch.
  const maps: UnlockNode[] = [
    {
      id: 'map-split-vein',
      kind: 'map',
      name: 'Split Vein',
      cost: 3,
      summary: 'A two-path map for free play: pressure from both veins at once.',
      requires: ['eco-purse-1'],
      payload: { k: 'map', mapId: 'split-vein' },
      position: polar(angleDeg - 10, 28),
    },
    {
      id: 'map-the-coil',
      kind: 'map',
      name: 'The Coil',
      cost: 5,
      summary: 'A long spiral for free play: one path, a lot of it.',
      requires: ['map-split-vein'],
      payload: { k: 'map', mapId: 'the-coil' },
      position: polar(angleDeg - 10, 40),
    },
  ];
  return [...chain, ...maps];
}

/** Defense spoke: Core, placements, and the global keystones at the deep end. */
function defenseSpoke(angleDeg: number): UnlockNode[] {
  const chain = spoke(angleDeg, [
    {
      id: 'def-core-1',
      kind: 'start',
      name: 'Reinforced Core',
      summary: 'The Core starts with +5 HP.',
      cost: 3,
      payload: { k: 'start', stat: 'coreHp', value: 5 },
    },
    {
      id: 'def-core-2',
      kind: 'start',
      name: 'Hardened Core',
      summary: 'The Core starts with +5 more HP.',
      cost: 4,
      payload: { k: 'start', stat: 'coreHp', value: 5 },
    },
    {
      id: 'def-placement',
      kind: 'start',
      name: 'Logistics',
      summary: 'One extra placement every build phase.',
      cost: 5,
      payload: { k: 'start', stat: 'placement', value: 1 },
    },
    {
      id: 'def-sharp',
      kind: 'effect',
      name: 'Whetstone',
      summary: 'Every tower deals +1 flat damage.',
      cost: 4,
      payload: {
        k: 'effect',
        effects: [
          { k: 'stat', target: { s: 'allTowers' }, stat: 'flatDamage', op: 'add', value: 1 },
        ],
      },
    },
    {
      id: 'def-pierce',
      kind: 'effect',
      name: 'Rend',
      summary: 'Every tower ignores 10% of armor.',
      cost: 5,
      payload: {
        k: 'effect',
        effects: [
          { k: 'stat', target: { s: 'allTowers' }, stat: 'armorPierce', op: 'add', value: 0.1 },
        ],
      },
    },
    {
      id: 'def-crit',
      kind: 'effect',
      name: 'Killer Instinct',
      summary: 'Every tower gains +3% crit chance.',
      cost: 5,
      payload: {
        k: 'effect',
        effects: [
          { k: 'stat', target: { s: 'allTowers' }, stat: 'critChance', op: 'add', value: 0.03 },
        ],
      },
    },
    {
      id: 'def-venom-cap',
      kind: 'effect',
      name: 'Saturation',
      summary: 'Poison stacks to 7 instead of 5, on every source.',
      cost: 5,
      payload: { k: 'effect', effects: [{ k: 'statusTuning', status: 'poison', capOverride: 7 }] },
    },
    {
      id: 'def-keystone',
      kind: 'effect',
      name: 'Critical Mass',
      summary: 'Class tiers activate one tower earlier: 1/3/5 instead of 2/4/6.',
      cost: 8,
      payload: { k: 'effect', effects: [{ k: 'thresholdShift', delta: -1 }] },
    },
  ]);
  // A two-node side branch off Whetstone, mirroring the economy spoke's map branch.
  const branch: UnlockNode[] = [
    {
      id: 'def-swift',
      kind: 'effect',
      name: 'Drilled Crews',
      cost: 5,
      summary: 'Every tower attacks 5% faster.',
      requires: ['def-sharp'],
      position: polar(angleDeg + 10, 28),
      payload: {
        k: 'effect',
        effects: [
          {
            k: 'stat',
            target: { s: 'allTowers' },
            stat: 'attackSpeedMultiplier',
            op: 'mul',
            value: 1.05,
          },
        ],
      },
    },
    {
      id: 'def-might',
      kind: 'effect',
      name: 'Standing Orders',
      cost: 5,
      summary: 'Every tower deals 5% more damage.',
      requires: ['def-swift'],
      position: polar(angleDeg + 10, 40),
      payload: {
        k: 'effect',
        effects: [
          {
            k: 'stat',
            target: { s: 'allTowers' },
            stat: 'damageMultiplier',
            op: 'mul',
            value: 1.05,
          },
        ],
      },
    },
  ];
  return [...chain, ...branch];
}

// ══ The 10x expansion (R-08-18b): ladders, techniques, synthesis ══════════════════════

/** Effect payload scoped to one class's placed towers. */
const fx = (forClass: ClassId, effects: SynergyEffect[]): UnlockNode['payload'] => ({
  k: 'effect',
  forClass,
  effects,
});
/** Global effect payload — class-free kinds only (see the header). */
const gfx = (effects: SynergyEffect[]): UnlockNode['payload'] => ({ k: 'effect', effects });

/** Ladder geometry: 17 one-Shard steps from r=22 out to ~44.8, the notable at 48. */
const LADDER_STEPS = 17;
const ladderRadius = (i: number): number => Math.round((22 + i * 1.425) * 10) / 10;

interface LadderSpec {
  key: string;
  title: string;
  /** Step i's content. Alternating flavours keep 17 nodes from reading as one row. */
  node: (i: number) => { summary: string; payload: UnlockNode['payload'] };
  peak: { name: string; summary: string; payload: UnlockNode['payload'] };
}

/** One chain of smalls ending in a notable, flanking a trunk at a fixed offset. */
function ladder(
  scopeId: string,
  displayName: string,
  angleDeg: number,
  offsetDeg: number,
  rootId: string,
  spec: LadderSpec,
): UnlockNode[] {
  const nodes: UnlockNode[] = [];
  for (let i = 0; i < LADDER_STEPS; i++) {
    const step = spec.node(i);
    nodes.push({
      id: `${scopeId}-${spec.key}-${i + 1}`,
      kind: step.payload.k === 'start' ? 'start' : 'effect',
      name: `${displayName} ${spec.title} ${i + 1}`,
      summary: step.summary,
      cost: 1,
      requires: [i === 0 ? rootId : `${scopeId}-${spec.key}-${i}`],
      payload: step.payload,
      position: polar(angleDeg + offsetDeg, ladderRadius(i)),
    });
  }
  nodes.push({
    id: `${scopeId}-${spec.key}-peak`,
    kind: spec.peak.payload.k === 'start' ? 'start' : 'effect',
    name: spec.peak.name,
    summary: spec.peak.summary,
    cost: 3,
    requires: [`${scopeId}-${spec.key}-${LADDER_STEPS}`],
    payload: spec.peak.payload,
    position: polar(angleDeg + offsetDeg, 48),
  });
  return nodes;
}

/** The trunk's first node — what every ladder in the wedge hangs off. */
const classEntryId = (classId: ClassId): string =>
  LOCKED_CLASS_COST[classId] !== undefined ? `class-${classId}` : `${classId}-power-1`;

/**
 * The Craft ladder's notable is the class's SECOND flavour node — hand-authored, like
 * FLAVOUR, because the deep end of a themed ladder should pay in identity, not math.
 */
const FLAVOUR2: Record<ClassId, { name: string; summary: string; payload: UnlockNode['payload'] }> =
  {
    warden: {
      name: 'Living Rampart',
      summary: 'The Core gains +4 max HP.',
      payload: gfx([run('bonusCoreHp', 'add', 4)]),
    },
    marksman: {
      name: 'Killzone',
      summary: 'Marksman towers reach a further +0.8 tiles.',
      payload: fx('marksman', [stat('rangeBonus', 'add', 0.8)]),
    },
    arcanist: {
      name: 'Nullfield',
      summary: 'Arcanist damage ignores a further 30% of armor.',
      payload: fx('arcanist', [stat('armorPierce', 'add', 0.3)]),
    },
    venom: {
      name: 'Toxic Resonance',
      summary: 'Venom towers hit Poisoned enemies 15% harder.',
      payload: fx('venom', [{ k: 'bonusVsStatus', status: 'poison', damageMultiplier: 1.15 }]),
    },
    frost: {
      name: 'Permafrost',
      summary: 'Frost towers hit Chilled enemies a further 15% harder.',
      payload: fx('frost', [{ k: 'bonusVsStatus', status: 'chill', damageMultiplier: 1.15 }]),
    },
    tempest: {
      name: 'Storm Front',
      summary: 'Tempest towers attack 10% faster.',
      payload: fx('tempest', [stat('attackSpeedMultiplier', 'mul', 1.1)]),
    },
    alchemist: {
      name: 'Golden Ratio',
      summary: 'Every cleared wave pays +1 gold.',
      payload: gfx([{ k: 'gold', amount: 1, trigger: 'waveClear' }]),
    },
    beacon: {
      name: 'Solar Lens',
      summary: 'Beacon crits hit +40% harder.',
      payload: fx('beacon', [stat('critMultiplier', 'add', 0.4)]),
    },
    swarm: {
      name: 'Endless Tide',
      summary: 'Swarm towers hit +2 harder and 5% faster.',
      payload: fx('swarm', [
        stat('flatDamage', 'add', 2),
        stat('attackSpeedMultiplier', 'mul', 1.05),
      ]),
    },
    void: {
      name: 'Null Hunger',
      summary: 'Void hits carve off a further 1% of max HP.',
      payload: fx('void', [stat('percentMaxHpDamage', 'add', 0.01)]),
    },
  };

/** Four ladders per class: Might/Tempo/Focus flank the trunk, Craft ends in FLAVOUR2. */
function classLadders(classId: ClassId, name: string, angle: number): UnlockNode[] {
  const rootId = classEntryId(classId);
  const might: LadderSpec = {
    key: 'might',
    title: 'Might',
    node: (i) =>
      i % 2 === 0
        ? {
            summary: `${name} towers hit for +1 flat damage.`,
            payload: fx(classId, [stat('flatDamage', 'add', 1)]),
          }
        : {
            summary: `${name} towers deal 3% more damage.`,
            payload: fx(classId, [stat('damageMultiplier', 'mul', 1.03)]),
          },
    peak: {
      name: `${name}'s Wrath`,
      summary: `${name} towers deal 10% more damage.`,
      payload: fx(classId, [stat('damageMultiplier', 'mul', 1.1)]),
    },
  };
  const tempo: LadderSpec = {
    key: 'tempo',
    title: 'Tempo',
    node: (i) =>
      i % 2 === 0
        ? {
            summary: `${name} towers attack 2% faster.`,
            payload: fx(classId, [stat('attackSpeedMultiplier', 'mul', 1.02)]),
          }
        : {
            summary: `${name} towers reach +0.1 tiles further.`,
            payload: fx(classId, [stat('rangeBonus', 'add', 0.1)]),
          },
    peak: {
      name: `${name}'s Rhythm`,
      summary: `${name} towers attack 8% faster.`,
      payload: fx(classId, [stat('attackSpeedMultiplier', 'mul', 1.08)]),
    },
  };
  const focus: LadderSpec = {
    key: 'focus',
    title: 'Focus',
    node: (i) =>
      i % 2 === 0
        ? {
            summary: `${name} towers gain +1% crit chance.`,
            payload: fx(classId, [stat('critChance', 'add', 0.01)]),
          }
        : {
            summary: `${name} crits hit +5% harder.`,
            payload: fx(classId, [stat('critMultiplier', 'add', 0.05)]),
          },
    peak: {
      name: `${name}'s Eye`,
      summary: `${name} towers gain +5% crit chance.`,
      payload: fx(classId, [stat('critChance', 'add', 0.05)]),
    },
  };
  const craft: LadderSpec = {
    key: 'craft',
    title: 'Craft',
    node: (i) =>
      i % 2 === 0
        ? {
            summary: `${name} towers reach +0.08 tiles further.`,
            payload: fx(classId, [stat('rangeBonus', 'add', 0.08)]),
          }
        : {
            summary: `${name} damage ignores +2% of armor.`,
            payload: fx(classId, [stat('armorPierce', 'add', 0.02)]),
          },
    peak: FLAVOUR2[classId],
  };
  return [
    ...ladder(classId, name, angle, -13, rootId, might),
    ...ladder(classId, name, angle, -7, rootId, tempo),
    ...ladder(classId, name, angle, 7, rootId, focus),
    ...ladder(classId, name, angle, 13, rootId, craft),
  ];
}

interface Technique {
  slug: string;
  name: string;
  summary: string;
  cost: number;
  payload: UnlockNode['payload'];
}

/**
 * Three hand-authored ability nodes per class — the everyNthShot/onKill/aura effects
 * that change how a class PLAYS, not just its numbers. They hang off mid-ladder, so
 * committing to a direction is what opens them.
 */
const TECHNIQUES: Record<ClassId, [Technique, Technique, Technique]> = {
  warden: [
    {
      slug: 'aegis',
      name: 'Aegis Protocol',
      summary: 'The Core gains +3 max HP — Warden engineering, applied at the foundation.',
      cost: 4,
      payload: gfx([run('bonusCoreHp', 'add', 3)]),
    },
    {
      slug: 'sentinel',
      name: 'Sentinel Watch',
      summary: 'Wardens steady their neighbours: towers within 2 tiles deal 5% more.',
      cost: 4,
      payload: fx('warden', [
        { k: 'aura', radiusTiles: 2, stat: 'damageMultiplier', op: 'mul', value: 1.05 },
      ]),
    },
    {
      slug: 'crushing',
      name: 'Crushing Blow',
      summary: 'Every 5th Warden shot lands twice as hard.',
      cost: 5,
      payload: fx('warden', [
        { k: 'everyNthShot', n: 5, then: [{ k: 'shotModifier', damageMultiplier: 2 }] },
      ]),
    },
  ],
  marksman: [
    {
      slug: 'deadeye',
      name: 'Deadeye Drill',
      summary: 'Every 4th Marksman shot punches through 2 extra enemies.',
      cost: 4,
      payload: fx('marksman', [
        { k: 'everyNthShot', n: 4, then: [{ k: 'shotModifier', pierceAdd: 2 }] },
      ]),
    },
    {
      slug: 'headhunter',
      name: 'Headhunter',
      summary: 'Marksman hits execute enemies below 8% of max HP.',
      cost: 5,
      payload: fx('marksman', [{ k: 'executeBelow', percentMaxHp: 0.08 }]),
    },
    {
      slug: 'spotter',
      name: 'Spotter Rounds',
      summary: 'Marksman hits Mark the target for 2s — Marked enemies are everyone’s business.',
      cost: 4,
      payload: fx('marksman', [
        { k: 'applyStatus', on: 'hit', status: 'mark', magnitude: 1, durationTicks: 120 },
      ]),
    },
  ],
  arcanist: [
    {
      slug: 'resonance',
      name: 'Sigil Resonance',
      summary: 'Arcanist damage hits Marked enemies 15% harder.',
      cost: 4,
      payload: fx('arcanist', [{ k: 'bonusVsStatus', status: 'mark', damageMultiplier: 1.15 }]),
    },
    {
      slug: 'overcharge',
      name: 'Overcharge',
      summary: 'Every 5th Arcanist shot detonates for triple damage.',
      cost: 5,
      payload: fx('arcanist', [
        { k: 'everyNthShot', n: 5, then: [{ k: 'shotModifier', damageMultiplier: 3 }] },
      ]),
    },
    {
      slug: 'farsight',
      name: 'Runic Farsight',
      summary: 'Arcanist range extends 15% — the sigil sees further than the eye.',
      cost: 4,
      payload: fx('arcanist', [stat('rangeMultiplier', 'mul', 1.15)]),
    },
  ],
  venom: [
    {
      slug: 'corrosive',
      name: 'Corrosive Blood',
      summary: 'Enemies dying with 3+ Poison stacks splash 1 stack over 2 tiles.',
      cost: 5,
      payload: fx('venom', [
        { k: 'onKillSpreadStatus', status: 'poison', radiusTiles: 2, stacks: 1, requireStacks: 3 },
      ]),
    },
    {
      slug: 'envenom',
      name: 'Envenom',
      summary: 'Venom hits apply one extra Poison stack.',
      cost: 4,
      payload: fx('venom', [
        {
          k: 'applyStatus',
          on: 'hit',
          status: 'poison',
          magnitude: 2,
          durationTicks: 240,
          stacks: 1,
        },
      ]),
    },
    {
      slug: 'fester',
      name: 'Fester',
      summary: 'Venom towers hit Poisoned enemies 12% harder.',
      cost: 4,
      payload: fx('venom', [{ k: 'bonusVsStatus', status: 'poison', damageMultiplier: 1.12 }]),
    },
  ],
  frost: [
    {
      slug: 'shatter',
      name: 'Shatter',
      summary: 'Frozen enemies take 50% more damage from Frost towers.',
      cost: 5,
      payload: fx('frost', [{ k: 'bonusVsStatus', status: 'freeze', damageMultiplier: 1.5 }]),
    },
    {
      slug: 'coldsnap',
      name: 'Cold Snap',
      summary: 'With Frost fielded, every 10s a cold snap Chills everything for 1.5s.',
      cost: 5,
      payload: fx('frost', [
        {
          k: 'periodic',
          everyTicks: 600,
          then: [{ k: 'fieldStatus', status: 'chill', durationTicks: 90, magnitude: 0.2 }],
        },
      ]),
    },
    {
      slug: 'glacial',
      name: 'Glacial Focus',
      summary: 'Frost crits hit +30% harder — cold and exact.',
      cost: 4,
      payload: fx('frost', [stat('critMultiplier', 'add', 0.3)]),
    },
  ],
  tempest: [
    {
      slug: 'stormcall',
      name: 'Stormcall',
      summary: 'With Tempest fielded, every 10s the sky answers: the field is Shocked for 1s.',
      cost: 5,
      payload: fx('tempest', [
        {
          k: 'periodic',
          everyTicks: 600,
          then: [{ k: 'fieldStatus', status: 'shock', durationTicks: 60, magnitude: 1 }],
        },
      ]),
    },
    {
      slug: 'fork',
      name: 'Fork Lightning',
      summary: 'Tempest chains hop to one more target.',
      cost: 5,
      payload: fx('tempest', [stat('chainCount', 'add', 1)]),
    },
    {
      slug: 'static',
      name: 'Static Charge',
      summary: 'Every Tempest hit adds a Shock stack — three stacks stun.',
      cost: 4,
      payload: fx('tempest', [
        {
          k: 'applyStatus',
          on: 'hit',
          status: 'shock',
          magnitude: 1,
          durationTicks: 180,
          stacks: 1,
        },
      ]),
    },
  ],
  alchemist: [
    {
      slug: 'transmute',
      name: 'Transmutation',
      summary: 'Kills have a 5% chance to transmute into +1 gold.',
      cost: 4,
      payload: gfx([{ k: 'gold', amount: 1, trigger: 'kill', chance: 0.05 }]),
    },
    {
      slug: 'edge',
      name: 'Philosopher’s Edge',
      summary: 'Alchemist towers deal 10% more damage — refined, not raw.',
      cost: 4,
      payload: fx('alchemist', [stat('damageMultiplier', 'mul', 1.1)]),
    },
    {
      slug: 'caustic',
      name: 'Caustic Mix',
      summary: 'Alchemist hits Wither the target for 2s, sapping 15% of its damage.',
      cost: 5,
      payload: fx('alchemist', [
        { k: 'applyStatus', on: 'hit', status: 'wither', magnitude: 0.15, durationTicks: 120 },
      ]),
    },
  ],
  beacon: [
    {
      slug: 'overwatch',
      name: 'Overwatch Beam',
      summary: 'Towers within a Beacon’s range gain +5% crit chance.',
      cost: 4,
      payload: fx('beacon', [
        {
          k: 'aura',
          radiusTiles: 2,
          radiusFromRange: true,
          stat: 'critChance',
          op: 'add',
          value: 0.05,
        },
      ]),
    },
    {
      slug: 'lens',
      name: 'Lens Array',
      summary: 'Beacon range extends 20% — light travels.',
      cost: 4,
      payload: fx('beacon', [stat('rangeMultiplier', 'mul', 1.2)]),
    },
    {
      slug: 'amplify',
      name: 'Amplify',
      summary: 'Every 3rd Beacon shot is focused for 50% more.',
      cost: 5,
      payload: fx('beacon', [
        { k: 'everyNthShot', n: 3, then: [{ k: 'shotModifier', damageMultiplier: 1.5 }] },
      ]),
    },
  ],
  swarm: [
    {
      slug: 'hivemind',
      name: 'Hive Mind',
      summary: 'Towers within 1.5 tiles of a Swarm attack 8% faster.',
      cost: 4,
      payload: fx('swarm', [
        { k: 'aura', radiusTiles: 1.5, stat: 'attackSpeedMultiplier', op: 'mul', value: 1.08 },
      ]),
    },
    {
      slug: 'overwhelm',
      name: 'Overwhelm',
      summary: 'Every other Swarm hit strikes 25% harder — numbers win.',
      cost: 4,
      payload: fx('swarm', [
        { k: 'everyNthShot', n: 2, then: [{ k: 'shotModifier', damageMultiplier: 1.25 }] },
      ]),
    },
    {
      slug: 'brood',
      name: 'Spawn Brood',
      summary: 'Swarm towers spawn a broodling every 10s: 3 dmg +20% of the owner’s, lives 5s.',
      cost: 5,
      payload: fx('swarm', [
        {
          k: 'summon',
          everyTicks: 600,
          flatDamage: 3,
          damageFromOwnerPercent: 0.2,
          lifetimeTicks: 300,
        },
      ]),
    },
  ],
  void: [
    {
      slug: 'horizon',
      name: 'Event Horizon',
      summary: 'Void hits erase enemies below 10% of max HP.',
      cost: 5,
      payload: fx('void', [{ k: 'executeBelow', percentMaxHp: 0.1 }]),
    },
    {
      slug: 'entropy',
      name: 'Entropy',
      summary: 'Void hits carve off an extra 1% of max HP.',
      cost: 4,
      payload: fx('void', [stat('percentMaxHpDamage', 'add', 0.01)]),
    },
    {
      slug: 'singularity',
      name: 'Singularity Round',
      summary: 'Every 6th Void shot collapses inward: double damage, pierces 1.',
      cost: 5,
      payload: fx('void', [
        {
          k: 'everyNthShot',
          n: 6,
          then: [{ k: 'shotModifier', damageMultiplier: 2, pierceAdd: 1 }],
        },
      ]),
    },
  ],
};

/** Technique slots: nestled between ladders, each gated on a mid-ladder commitment. */
const TECH_SLOTS: { offset: number; radius: number; req: (scope: string) => string }[] = [
  { offset: -10, radius: 33, req: (scope) => `${scope}-might-8` },
  { offset: 10, radius: 33, req: (scope) => `${scope}-focus-8` },
  { offset: -4, radius: 41, req: (scope) => `${scope}-tempo-12` },
];

function classTechniques(classId: ClassId, angle: number): UnlockNode[] {
  return TECHNIQUES[classId].map((tech, i) => {
    const slot = TECH_SLOTS[i] as (typeof TECH_SLOTS)[number];
    return {
      id: `${classId}-${tech.slug}`,
      kind: 'effect' as const,
      name: tech.name,
      summary: tech.summary,
      cost: tech.cost,
      requires: [slot.req(classId)],
      payload: tech.payload,
      position: polar(angle + slot.offset, slot.radius),
    };
  });
}

/** Economy wedge ladders + two ability nodes, mirroring the class-wedge anatomy. */
function economyExpansion(angle: number): UnlockNode[] {
  const fortune: LadderSpec = {
    key: 'fortune',
    title: 'Fortune',
    node: () => ({
      summary: 'Kills are 0.5% likelier to drop bonus gold.',
      payload: gfx([run('goldDropChance', 'add', 0.005)]),
    }),
    peak: {
      name: 'Fortune’s Wheel',
      summary: 'Kills are a further 3% likelier to drop bonus gold.',
      payload: gfx([run('goldDropChance', 'add', 0.03)]),
    },
  };
  const thrift: LadderSpec = {
    key: 'thrift',
    title: 'Thrift',
    node: () => ({
      summary: 'Shop rerolls cost 1% less.',
      payload: gfx([run('rerollCostMultiplier', 'mul', 0.99)]),
    }),
    peak: {
      name: 'Wholesale',
      summary: 'Shop rerolls cost a further 5% less.',
      payload: gfx([run('rerollCostMultiplier', 'mul', 0.95)]),
    },
  };
  const stockpile: LadderSpec = {
    key: 'stockpile',
    title: 'Stockpile',
    node: () => ({
      summary: 'Free-play runs start with +1 gold.',
      payload: { k: 'start', stat: 'gold', value: 1 },
    }),
    peak: {
      name: 'Royal Treasury',
      summary: 'Free-play runs start with +5 more gold.',
      payload: { k: 'start', stat: 'gold', value: 5 },
    },
  };
  const abilities: UnlockNode[] = [
    {
      id: 'eco-golden-touch',
      kind: 'effect',
      name: 'Golden Touch',
      summary: 'Kills have a 4% chance to pay +1 gold.',
      cost: 4,
      requires: ['eco-interest'],
      payload: gfx([{ k: 'gold', amount: 1, trigger: 'kill', chance: 0.04 }]),
      position: polar(angle + 13, 30),
    },
    {
      id: 'eco-market-instinct',
      kind: 'effect',
      name: 'Market Instinct',
      summary: 'Shop rerolls cost 10% less.',
      cost: 4,
      requires: ['eco-golden-touch'],
      payload: gfx([run('rerollCostMultiplier', 'mul', 0.9)]),
      position: polar(angle + 13, 38),
    },
  ];
  return [
    ...ladder('eco', 'Gold', angle, -13, 'eco-purse-1', fortune),
    ...ladder('eco', 'Gold', angle, -7, 'eco-purse-1', thrift),
    ...ladder('eco', 'Gold', angle, 7, 'eco-purse-1', stockpile),
    ...abilities,
  ];
}

/** Defense wedge ladders + two ability nodes. */
function defenseExpansion(angle: number): UnlockNode[] {
  const bulwark: LadderSpec = {
    key: 'bulwark',
    title: 'Bulwark',
    node: () => ({
      summary: 'The Core starts with +1 HP.',
      payload: { k: 'start', stat: 'coreHp', value: 1 },
    }),
    peak: {
      name: 'Bedrock',
      summary: 'The Core starts with +4 more HP.',
      payload: { k: 'start', stat: 'coreHp', value: 4 },
    },
  };
  const arsenal: LadderSpec = {
    key: 'arsenal',
    title: 'Arsenal',
    node: (i) =>
      i % 2 === 0
        ? {
            summary: 'Every tower hits for +0.5 flat damage.',
            payload: gfx([
              { k: 'stat', target: { s: 'allTowers' }, stat: 'flatDamage', op: 'add', value: 0.5 },
            ]),
          }
        : {
            summary: 'Every tower deals 1% more damage.',
            payload: gfx([
              {
                k: 'stat',
                target: { s: 'allTowers' },
                stat: 'damageMultiplier',
                op: 'mul',
                value: 1.01,
              },
            ]),
          },
    peak: {
      name: 'Full Armory',
      summary: 'Every tower deals 5% more damage.',
      payload: gfx([
        {
          k: 'stat',
          target: { s: 'allTowers' },
          stat: 'damageMultiplier',
          op: 'mul',
          value: 1.05,
        },
      ]),
    },
  };
  const corps: LadderSpec = {
    key: 'corps',
    title: 'Corps',
    node: (i) =>
      i % 2 === 0
        ? {
            summary: 'Every tower gains +0.5% crit chance.',
            payload: gfx([
              { k: 'stat', target: { s: 'allTowers' }, stat: 'critChance', op: 'add', value: 0.005 },
            ]),
          }
        : {
            summary: 'Every tower’s crits hit +2% harder.',
            payload: gfx([
              {
                k: 'stat',
                target: { s: 'allTowers' },
                stat: 'critMultiplier',
                op: 'add',
                value: 0.02,
              },
            ]),
          },
    peak: {
      name: 'Elite Corps',
      summary: 'Every tower gains +3% crit chance.',
      payload: gfx([
        { k: 'stat', target: { s: 'allTowers' }, stat: 'critChance', op: 'add', value: 0.03 },
      ]),
    },
  };
  const abilities: UnlockNode[] = [
    {
      id: 'def-last-stand',
      kind: 'effect',
      name: 'Last Stand',
      summary: 'The Core gains +5 max HP.',
      cost: 4,
      requires: ['def-sharp'],
      payload: gfx([run('bonusCoreHp', 'add', 5)]),
      position: polar(angle - 13, 30),
    },
    {
      id: 'def-garrison',
      kind: 'effect',
      name: 'Garrison',
      summary: 'One extra placement every build phase.',
      cost: 5,
      requires: ['def-last-stand'],
      payload: gfx([run('bonusPlacements', 'add', 1)]),
      position: polar(angle - 13, 38),
    },
  ];
  return [
    ...ladder('def', 'Guard', angle, -7, 'def-core-1', bulwark),
    ...ladder('def', 'Guard', angle, 7, 'def-core-1', arsenal),
    ...ladder('def', 'Guard', angle, 13, 'def-core-1', corps),
    ...abilities,
  ];
}

// ── Synthesis: the connecting points (R-08-18b) ───────────────────────────────────────

/** Every wedge in circle order, class or utility, with its trunk anchors. */
type WedgeKey = ClassId | 'eco' | 'def';
const midTrunkId = (w: WedgeKey): string =>
  w === 'eco' ? 'eco-interest' : w === 'def' ? 'def-placement' : `${w}-reach`;
const keystoneId = (w: WedgeKey): string => `${w}-keystone`;

interface SynthesisPair {
  a: WedgeKey;
  b: WedgeKey;
  /** Boundary angle: halfway between the two wedges. */
  angle: number;
  confluence: { name: string; summary: string; payload: UnlockNode['payload'] };
  apex: { name: string; summary: string; payload: UnlockNode['payload'] };
}

/**
 * The 12 seams of the circle. A Confluence opens when you hold BOTH neighbouring
 * trunks three-deep; an Apex when you hold BOTH keystones. Cross-class abilities
 * scope to one side and reference the other's status — see the header's forClass
 * constraint for why.
 */
const SYNTHESIS: SynthesisPair[] = [
  {
    a: 'warden',
    b: 'marksman',
    angle: 285,
    confluence: {
      name: 'Overwatch Bastion',
      summary: 'With Warden ground held, every 5th Marksman shot pierces 1 and hits 50% harder.',
      payload: fx('marksman', [
        { k: 'everyNthShot', n: 5, then: [{ k: 'shotModifier', pierceAdd: 1, damageMultiplier: 1.5 }] },
      ]),
    },
    apex: {
      name: 'Citadel Doctrine',
      summary: 'Every tower reaches +0.3 tiles — the citadel sees all.',
      payload: gfx([
        { k: 'stat', target: { s: 'allTowers' }, stat: 'rangeBonus', op: 'add', value: 0.3 },
      ]),
    },
  },
  {
    a: 'marksman',
    b: 'arcanist',
    angle: 315,
    confluence: {
      name: 'Marked for Death',
      summary: 'Marksman shots hit Marked enemies 25% harder.',
      payload: fx('marksman', [{ k: 'bonusVsStatus', status: 'mark', damageMultiplier: 1.25 }]),
    },
    apex: {
      name: 'Arcane Ballistics',
      summary: 'Marksman shots Mark their target for 4s.',
      payload: fx('marksman', [
        { k: 'applyStatus', on: 'hit', status: 'mark', magnitude: 1, durationTicks: 240 },
      ]),
    },
  },
  {
    a: 'arcanist',
    b: 'venom',
    angle: 345,
    confluence: {
      name: 'Blight Sigil',
      summary: 'Arcanist hits apply a Poison stack — the sigil festers.',
      payload: fx('arcanist', [
        {
          k: 'applyStatus',
          on: 'hit',
          status: 'poison',
          magnitude: 2,
          durationTicks: 240,
          stacks: 1,
        },
      ]),
    },
    apex: {
      name: 'Plague Rune',
      summary: 'Poison stacks to 9, on every source.',
      payload: gfx([{ k: 'statusTuning', status: 'poison', capOverride: 9 }]),
    },
  },
  {
    a: 'venom',
    b: 'frost',
    angle: 15,
    confluence: {
      name: 'Cryotoxin',
      summary: 'Venom towers hit Chilled enemies 25% harder.',
      payload: fx('venom', [{ k: 'bonusVsStatus', status: 'chill', damageMultiplier: 1.25 }]),
    },
    apex: {
      name: 'Absolute Rot',
      summary: 'Venom hits also Chill: -15% speed for 1.5s.',
      payload: fx('venom', [
        { k: 'applyStatus', on: 'hit', status: 'chill', magnitude: 0.15, durationTicks: 90 },
      ]),
    },
  },
  {
    a: 'frost',
    b: 'tempest',
    angle: 45,
    confluence: {
      name: 'Ice Lightning',
      summary: 'Frost hits add a Shock stack — three stacks stun.',
      payload: fx('frost', [
        {
          k: 'applyStatus',
          on: 'hit',
          status: 'shock',
          magnitude: 1,
          durationTicks: 180,
          stacks: 1,
        },
      ]),
    },
    apex: {
      name: 'Ion Glacier',
      summary: 'With Frost fielded, every 15s the field flash-freezes for 0.5s.',
      payload: fx('frost', [
        {
          k: 'periodic',
          everyTicks: 900,
          then: [{ k: 'fieldStatus', status: 'freeze', durationTicks: 30, magnitude: 1 }],
        },
      ]),
    },
  },
  {
    a: 'tempest',
    b: 'alchemist',
    angle: 75,
    confluence: {
      name: 'Volatile Reagents',
      summary: 'Alchemist attacks chain to one extra target.',
      payload: fx('alchemist', [stat('chainCount', 'add', 1)]),
    },
    apex: {
      name: 'Storm Alembic',
      summary: 'Shop rerolls cost 15% less, distilled from the storm.',
      payload: gfx([run('rerollCostMultiplier', 'mul', 0.85)]),
    },
  },
  {
    a: 'alchemist',
    b: 'beacon',
    angle: 105,
    confluence: {
      name: 'Gilded Optics',
      summary: 'Kills have a 6% chance to pay +1 gold — Beacon-guided ledgers.',
      payload: gfx([{ k: 'gold', amount: 1, trigger: 'kill', chance: 0.06 }]),
    },
    apex: {
      name: 'Prism Refinery',
      summary: 'Every cleared wave pays +2 gold, refined and split.',
      payload: gfx([{ k: 'gold', amount: 2, trigger: 'waveClear' }]),
    },
  },
  {
    a: 'beacon',
    b: 'swarm',
    angle: 135,
    confluence: {
      name: 'Rally Signal',
      summary: 'Swarm towers carry the Beacon’s light: +3% crit within 1.5 tiles.',
      payload: fx('swarm', [
        { k: 'aura', radiusTiles: 1.5, stat: 'critChance', op: 'add', value: 0.03 },
      ]),
    },
    apex: {
      name: 'Choir of Wings',
      summary: 'Swarm towers attack 12% faster, moving as one.',
      payload: fx('swarm', [stat('attackSpeedMultiplier', 'mul', 1.12)]),
    },
  },
  {
    a: 'swarm',
    b: 'void',
    angle: 165,
    confluence: {
      name: 'Devouring Cloud',
      summary: 'Swarm hits gnaw an extra 0.5% of max HP.',
      payload: fx('swarm', [stat('percentMaxHpDamage', 'add', 0.005)]),
    },
    apex: {
      name: 'Locust Singularity',
      summary: 'Void hits chain to one extra target.',
      payload: fx('void', [stat('chainCount', 'add', 1)]),
    },
  },
  {
    a: 'void',
    b: 'eco',
    angle: 195,
    confluence: {
      name: 'Profitable Oblivion',
      summary: 'The Void banks what it eats: interest pays on one more gold.',
      payload: gfx([run('interestCap', 'add', 1)]),
    },
    apex: {
      name: 'Entropy Dividend',
      summary: 'Kills are 8% likelier to drop bonus gold.',
      payload: gfx([run('goldDropChance', 'add', 0.08)]),
    },
  },
  {
    a: 'eco',
    b: 'def',
    angle: 225,
    confluence: {
      name: 'War Economy',
      summary: 'Waves pay +1 gold and the Core gains +2 HP.',
      payload: gfx([run('goldPerWave', 'add', 1), run('bonusCoreHp', 'add', 2)]),
    },
    apex: {
      name: 'Deep Coffers',
      summary: 'Interest covers one more gold and waves pay +1.',
      payload: gfx([run('interestCap', 'add', 1), run('goldPerWave', 'add', 1)]),
    },
  },
  {
    a: 'def',
    b: 'warden',
    angle: 255,
    confluence: {
      name: 'Shield Wall',
      summary: 'Every tower learns the Warden drill: +5% armor pierce.',
      payload: gfx([
        { k: 'stat', target: { s: 'allTowers' }, stat: 'armorPierce', op: 'add', value: 0.05 },
      ]),
    },
    apex: {
      name: 'Unbreakable',
      summary: 'The Core gains +6 max HP.',
      payload: gfx([run('bonusCoreHp', 'add', 6)]),
    },
  },
];

function synthesisNodes(): UnlockNode[] {
  return SYNTHESIS.flatMap((pair) => {
    const confId = `syn-${pair.a}-${pair.b}`;
    return [
      {
        id: confId,
        kind: 'effect' as const,
        name: pair.confluence.name,
        summary: pair.confluence.summary,
        cost: 6,
        // The AND-gate: three-deep in BOTH neighbouring trunks.
        requires: [midTrunkId(pair.a), midTrunkId(pair.b)],
        payload: pair.confluence.payload,
        position: polar(pair.angle, 20),
      },
      {
        id: `apex-${pair.a}-${pair.b}`,
        kind: 'effect' as const,
        name: pair.apex.name,
        summary: pair.apex.summary,
        cost: 10,
        // The deep gate: both keystones AND the seam's Confluence.
        requires: [confId, keystoneId(pair.a), keystoneId(pair.b)],
        payload: pair.apex.payload,
        position: polar(pair.angle, 49),
      },
    ];
  });
}

/** Ten class spokes at 30° steps, then the two utility spokes at the remaining slots. */
const CLASS_ANGLES: [ClassId, number][] = [
  ['warden', 270],
  ['marksman', 300],
  ['arcanist', 330],
  ['venom', 0],
  ['frost', 30],
  ['tempest', 60],
  ['alchemist', 90],
  ['beacon', 120],
  ['swarm', 150],
  ['void', 180],
];

export const UNLOCK_TREE: UnlockNode[] = [
  ...CLASS_ANGLES.flatMap(([classId, angle]) => {
    const name = CLASSES.find((c) => c.id === classId)?.name ?? classId;
    return [
      ...classSpoke(classId, angle),
      ...classLadders(classId, name, angle),
      ...classTechniques(classId, angle),
    ];
  }),
  ...economySpoke(210),
  ...economyExpansion(210),
  ...defenseSpoke(240),
  ...defenseExpansion(240),
  ...synthesisNodes(),
];

/**
 * What a fresh save can play (R-08-19): four classes and The Furrow — a complete,
 * winnable game on its own. The tree adds breadth, never the ability to win.
 */
export const STARTING_CLASSES: readonly ClassId[] = ['warden', 'marksman', 'venom', 'frost'];
export const STARTING_MAPS: readonly string[] = ['the-furrow'];

/**
 * R-08-19a — what "unlocked" means for a TOWER, decided here because R-08-19's own
 * arithmetic does not survive contact with the class pairing: every tower carries two
 * classes (R-02-06), so no four classes own twelve towers exactly.
 *
 * A tower is available when AT LEAST ONE of its classes is unlocked. The alternative —
 * requiring both — breaks R-08-20's own validation: a newly unlocked class adjacent to
 * still-locked classes would offer fewer than 6 towers and its tier-6 would be
 * unreachable, which is the dead-content failure V-2 exists to prevent. Locked classes
 * DO accumulate counts from spillover towers, but their tiers never fire because a
 * run's classTable carries only unlocked classes.
 */
export function unlockedTowerIds(classesUnlocked: readonly ClassId[]): string[] {
  const set = new Set<string>(classesUnlocked);
  return TOWERS.filter((t) => t.classes.some((c) => set.has(c))).map((t) => t.id);
}

/** Everything a set of owned tree nodes grants, starting state included. */
export interface UnlockedContent {
  classes: ClassId[];
  towers: string[];
  maps: string[];
  startBonuses: { stat: 'gold' | 'benchSlot' | 'coreHp' | 'placement'; value: number }[];
  /** Effect-node bundles, ready to hand to `RunConfig.runEffects` for free play. */
  runEffects: { forClass?: ClassId; label?: string; effects: readonly SynergyEffect[] }[];
}

export function unlockedContent(ownedNodeIds: readonly string[]): UnlockedContent {
  const owned = new Set(ownedNodeIds);
  const classes: ClassId[] = [...STARTING_CLASSES];
  const maps: string[] = [...STARTING_MAPS];
  const startBonuses: UnlockedContent['startBonuses'] = [];
  const runEffects: UnlockedContent['runEffects'] = [];

  for (const node of UNLOCK_TREE) {
    if (!owned.has(node.id)) continue;
    const p = node.payload;
    if (p.k === 'class') classes.push(p.classId);
    else if (p.k === 'map') maps.push(p.mapId);
    else if (p.k === 'start') startBonuses.push({ stat: p.stat, value: p.value });
    else if (p.k === 'effect') {
      // Labelled by the node's name, so the inspector's breakdown can say
      // "+2 Frost Impact" instead of an anonymous "Meta" (R-09-21).
      runEffects.push(
        p.forClass === undefined
          ? { label: node.name, effects: p.effects }
          : { forClass: p.forClass, label: node.name, effects: p.effects },
      );
    }
    // p.k === 'tower' has no instances in the shipped tree; classes carry the towers.
  }

  return { classes, towers: unlockedTowerIds(classes), maps, startBonuses, runEffects };
}

/**
 * The daily-run exception (R-08-22): dailies play with EVERYTHING, always, and with NO
 * tree effects, no matter what the player owns. Otherwise the leaderboard measures
 * unlock progress instead of skill — and the server, which derives the config purely
 * from the seed, could never reproduce a run shaped by a tree it cannot see.
 */
export const DAILY_UNLOCKS: {
  classes: readonly ClassId[];
  towers: readonly string[];
  startBonuses: readonly never[];
} = {
  classes: [
    'warden',
    'marksman',
    'arcanist',
    'venom',
    'frost',
    'tempest',
    'alchemist',
    'beacon',
    'swarm',
    'void',
  ],
  towers: TOWERS.map((t) => t.id),
  startBonuses: [],
};
